import { Link } from "react-router";
import { usePermissao } from "@/lib/use-permissao";
import { Compass, HelpCircle, LogOut, Moon, Settings, Shield, Sun, User } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { useTheme } from "@/lib/theme";
import { env } from "@/lib/env";
import { cn } from "@/lib/cn";
import { useProductTourContext } from "@/components/product-tour/ProductTourProvider";

export type OpcoesDoPerfilProps = {
  /** Fecha o popover (desktop) ou a gaveta (celular) depois de escolher. */
  aoEscolher: () => void;
  /** O tour existe pra esta organização. */
  tourDisponivel?: boolean;
  /** Ainda não foi visto — mostra o pontinho. */
  mostrarSeloDoTour?: boolean;
  onIniciarTour?: () => void;
  /** Linhas mais altas na gaveta do celular — alvo de toque de 44px. */
  grande?: boolean;
};

/** O conteúdo do menu do perfil — o mesmo no popover do desktop e na gaveta do celular. */
export function OpcoesDoPerfil({ aoEscolher, tourDisponivel, mostrarSeloDoTour, onIniciarTour, grande }: OpcoesDoPerfilProps) {
  const podeConfigurar = usePermissao("settings.manage");
  const { user, signOut } = useAuth();
  const { tourDaPagina, startPageTour } = useProductTourContext();
  const { theme, toggle } = useTheme();
  const isDark = theme === "dark";
  const item = cn(
    "flex w-full items-center gap-2.5 rounded-md px-2.5 text-sm text-foreground/90 transition-colors hover:bg-muted",
    grande ? "py-3" : "py-2",
  );

  return (
    <div className="p-1.5">
      <Link to="/conta" onClick={aoEscolher} className={item}>
        <User className="h-4 w-4 text-muted-foreground" />
        Minha conta
      </Link>
      {/* Configurações são da organização: só pra quem administra. */}
      {podeConfigurar && (
        <Link to="/configuracoes" onClick={aoEscolher} className={item}>
          <Settings className="h-4 w-4 text-muted-foreground" />
          Configurações
        </Link>
      )}

      {tourDaPagina && (
        <button
          type="button"
          onClick={() => {
            aoEscolher();
            tourDaPagina.onBeforeStart?.();
            startPageTour(tourDaPagina.steps);
          }}
          className={item}
        >
          <HelpCircle className="h-4 w-4 text-muted-foreground" />
          Tour desta página
        </button>
      )}

      {tourDisponivel && onIniciarTour && (
        <button
          type="button"
          onClick={() => {
            aoEscolher();
            onIniciarTour();
          }}
          className={item}
        >
          <Compass className="h-4 w-4 text-muted-foreground" />
          <span className="flex-1 text-left">Conheça a plataforma</span>
          {mostrarSeloDoTour && <span className="h-2 w-2 shrink-0 rounded-full bg-brand-600" />}
        </button>
      )}

      <div className="my-1 h-px bg-border" />

      <button type="button" onClick={toggle} className={item}>
        <span className="relative h-4 w-4">
          <Sun
            className={cn(
              "absolute inset-0 h-4 w-4 text-muted-foreground transition-all duration-300",
              isDark ? "scale-0 -rotate-90 opacity-0" : "scale-100 rotate-0 opacity-100",
            )}
          />
          <Moon
            className={cn(
              "absolute inset-0 h-4 w-4 text-muted-foreground transition-all duration-300",
              isDark ? "scale-100 rotate-0 opacity-100" : "scale-0 rotate-90 opacity-0",
            )}
          />
        </span>
        <span className="flex-1 text-left">{isDark ? "Tema claro" : "Tema escuro"}</span>
        <span className="rounded-full border border-zinc-200 bg-zinc-50 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider text-zinc-600 dark:border-border dark:bg-muted dark:text-muted-foreground">
          {isDark ? "Dark" : "Light"}
        </span>
      </button>

      {user?.platformRole === "ADMIN" && (
        <>
          <div className="my-1 h-px bg-border" />
          <a
            href={env.adminUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={aoEscolher}
            className={cn(item, "bg-primary/10 font-medium text-primary hover:bg-primary/15")}
          >
            <Shield className="h-4 w-4" />
            <span className="flex-1">Painel admin</span>
            <span className="rounded-full bg-primary px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-primary-foreground">
              ADMIN
            </span>
          </a>
        </>
      )}
      <div className="my-1 h-px bg-border" />
      <button
        type="button"
        onClick={() => signOut()}
        className={cn(item, "text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-500/10")}
      >
        <LogOut className="h-4 w-4" />
        Sair
      </button>
    </div>
  );
}
